/**
 * 価格表 (Excel / CSV) → CatalogItem のパーサ。
 *
 * ★ 固定列インデックスを使わない。ヘッダー行のラベルから列を解決する。
 *
 * 経緯 (2026-08-18):
 *   価格表フォーマットに B 列「製品カテゴリ」が挿入され、B 以降が 1 列ずつ右にずれた。
 *   旧実装は `COL = { productName: 0, costPriceJpy: 3, ... }` の固定インデックスだったため、
 *   新フォーマットを通すと「仕入価格(EUR) を 仕入価格(JPY) として保存」等の静かな誤取込になる。
 *   同じ事故を次のカラム追加でも繰り返さないよう、ヘッダー名ベースの解決に置き換えた。
 *
 * 対応フォーマット:
 *   旧: 製品名 / 輸入元販売価格(EUR) / 仕入価格(EUR) / 仕入価格(JPY) / 税抜価格(JPY) /
 *       割引前税込価格(JPY) / 税込価格(JPY) / 粗利率 / 割引率 / 備考
 *   新: 製品名 / 製品カテゴリ / 輸入元販売価格(EUR) / 仕入価格(EUR) / 仕入価格(JPY) /
 *       税抜価格(JPY) / 割引前税込価格(JPY) / 税込価格(JPY) / 粗利率 / 割引率 / 備考
 *   どちらもヘッダー名で解決するため、列順が変わっても列が増えても壊れない。
 *
 * ヘッダーが見つからない場合のみ、旧フォーマットの固定列にフォールバックする
 * (ヘッダー無し CSV を投入していた従来挙動の互換維持。UI 側で警告を出すこと)。
 */

/** パース結果 1 行 = CatalogItem 1 件の候補。 */
export interface ParsedCatalogRow {
  /** 元シート上の 0-indexed 行番号 (エラー表示用) */
  rowIndex: number;
  name: string;
  modelName: string;
  variation: string | null;
  sku: string;
  /**
   * 価格表の「製品カテゴリ」列 (Module / Hardware / TimeWaver Pro ...)。
   * ★ 商談の「商品カテゴリー」(ProductCategory コレクション) とは全く別物。
   */
  productCategory: string | null;
  description: string | null;
  supplierName: string | null;
  costPrice: number;
  priceExcludingTax: number;
  priceIncludingTax: number;
  /** null = 取込可能。文字列 = この行は取込対象外 */
  validationError: string | null;
  /** シートに SKU 列があってそれを採用したか (false = 製品名から自動生成) */
  skuFromSheet: boolean;
}

/** 解決された列インデックス。未解決は null。 */
export interface ResolvedColumns {
  name: number | null;
  productCategory: number | null;
  modelName: number | null;
  variation: number | null;
  sku: number | null;
  costPrice: number | null;
  priceExcludingTax: number | null;
  priceIncludingTax: number | null;
  description: number | null;
  supplierName: number | null;
}

export interface ParsedSheet {
  rows: ParsedCatalogRow[];
  /** 検出したヘッダー行 (0-indexed)。-1 = 未検出 (旧固定列にフォールバック) */
  headerRowIndex: number;
  columns: ResolvedColumns;
  /** ヘッダー行のラベルそのまま (UI のマッピング表示用) */
  headerLabels: string[];
  /** UI に出すべき注意 (ヘッダー未検出 / 必須列欠落 など) */
  warnings: string[];
}

type FieldKey = keyof ResolvedColumns;

/**
 * 列ラベルの正規化。
 * NFKC で全角英数字・全角カッコを半角化し、空白を除去して小文字化する。
 * これで「税抜価格 (JPY)」「税抜価格（ＪＰＹ）」「税抜価格(jpy)」が同一キーになる。
 *
 * 全角スペース (U+3000) は NFKC が半角スペースへ畳むため、\s だけで拾える。
 */
function normalizeHeader(v: unknown): string {
  const s =
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  return s
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .toLowerCase();
}

/**
 * 列マッチ規則。
 *  - exact:    正規化後の完全一致 (最優先)
 *  - contains: 部分一致 (exact で解決しなかった場合のみ)
 *  - exclude:  contains 判定時にこの語を含む列は除外
 *
 * ★ exclude が肝。素朴に contains('税込') すると
 *   「割引前税込価格(JPY)」が「税込価格(JPY)」より先にヒットし得るし、
 *   contains('仕入価格') は「仕入価格(EUR)」を掴んでしまう。
 */
const COLUMN_SPECS: Array<{
  key: FieldKey;
  exact: string[];
  contains: string[];
  exclude: string[];
}> = [
  {
    key: 'name',
    exact: ['製品名', '商品名', 'productname', 'name'],
    contains: ['製品名', '商品名'],
    exclude: ['カテゴリ'],
  },
  {
    key: 'productCategory',
    exact: [
      '製品カテゴリ',
      '製品カテゴリー',
      '商品カテゴリ',
      '商品カテゴリー',
      'カテゴリ',
      'カテゴリー',
      'productcategory',
      'category',
    ],
    contains: ['カテゴリ', 'category'],
    exclude: [],
  },
  {
    key: 'sku',
    exact: ['sku', 'skuコード', '商品コード', '製品コード'],
    contains: ['sku'],
    exclude: [],
  },
  {
    key: 'modelName',
    exact: ['型番', '品番', 'モデル名', 'model', 'modelname'],
    contains: ['型番', '品番'],
    exclude: [],
  },
  {
    key: 'variation',
    exact: ['バリエーション', 'variation'],
    contains: ['バリエーション'],
    exclude: [],
  },
  {
    key: 'costPrice',
    // 「仕入価格(EUR)」と区別するため JPY 明示を最優先で当てる
    exact: ['仕入価格(jpy)', '仕入価格(円)', '仕入価格', '仕入値', '原価'],
    contains: ['仕入価格', '原価'],
    exclude: ['eur', 'usd', 'chf', 'gbp', '€', '$'],
  },
  {
    key: 'priceExcludingTax',
    exact: ['税抜価格(jpy)', '税抜価格', '税抜き価格', '税抜', '税抜金額'],
    contains: ['税抜'],
    exclude: ['eur', 'usd', 'chf', 'gbp', '€', '$', '割引前'],
  },
  {
    key: 'priceIncludingTax',
    // 「割引前税込価格(JPY)」を絶対に拾わないこと (割引後の実売価格が正)
    exact: ['税込価格(jpy)', '税込価格', '税込み価格', '税込', '税込金額'],
    contains: ['税込'],
    exclude: ['eur', 'usd', 'chf', 'gbp', '€', '$', '割引前', '割前'],
  },
  {
    key: 'description',
    exact: ['備考', '説明', '摘要', 'remarks', 'note', 'notes', 'description'],
    contains: ['備考', '摘要'],
    exclude: [],
  },
  {
    key: 'supplierName',
    exact: ['仕入先', '仕入先名', 'メーカー', 'supplier', 'suppliername'],
    contains: ['仕入先'],
    exclude: ['価格', 'eur', 'jpy'],
  },
];

/** ヘッダー未検出時のフォールバック = 旧 (製品カテゴリ列が無い) フォーマットの固定列。 */
const LEGACY_COLUMNS: ResolvedColumns = {
  name: 0,
  productCategory: null,
  modelName: null,
  variation: null,
  sku: null,
  costPrice: 3,
  priceExcludingTax: 4,
  priceIncludingTax: 6,
  description: 9,
  supplierName: null,
};

const EMPTY_COLUMNS: ResolvedColumns = {
  name: null,
  productCategory: null,
  modelName: null,
  variation: null,
  sku: null,
  costPrice: null,
  priceExcludingTax: null,
  priceIncludingTax: null,
  description: null,
  supplierName: null,
};

/** 1 行分のラベルから列マップを解決する。 */
function resolveColumns(headerRow: unknown[]): ResolvedColumns {
  const labels = headerRow.map(normalizeHeader);
  const cols: ResolvedColumns = { ...EMPTY_COLUMNS };
  const taken = new Set<number>();

  // 1st pass: 完全一致 (alias の記述順が優先度)
  for (const spec of COLUMN_SPECS) {
    for (const alias of spec.exact) {
      const idx = labels.findIndex((l, i) => l === alias && !taken.has(i));
      if (idx >= 0) {
        cols[spec.key] = idx;
        taken.add(idx);
        break;
      }
    }
  }

  // 2nd pass: 部分一致 (未解決の項目のみ / exclude 語を含む列は対象外)
  for (const spec of COLUMN_SPECS) {
    if (cols[spec.key] != null) continue;
    for (const needle of spec.contains) {
      const idx = labels.findIndex(
        (l, i) =>
          !taken.has(i) &&
          l.includes(needle) &&
          !spec.exclude.some((ex) => l.includes(ex)),
      );
      if (idx >= 0) {
        cols[spec.key] = idx;
        taken.add(idx);
        break;
      }
    }
  }

  return cols;
}

/** 列マップの「確からしさ」。ヘッダー行を選ぶスコアに使う。 */
function scoreColumns(cols: ResolvedColumns): number {
  if (cols.name == null) return 0;
  let score = 1;
  if (cols.priceExcludingTax != null) score += 2;
  if (cols.priceIncludingTax != null) score += 2;
  if (cols.costPrice != null) score += 1;
  if (cols.productCategory != null) score += 1;
  if (cols.description != null) score += 1;
  if (cols.sku != null) score += 1;
  return score;
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const cleaned = v
      .normalize('NFKC')
      .replace(/[,¥\s]/g, '')
      .replace(/[^0-9.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.') return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function str(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  return null;
}

/** 指定列の値を取り出す (列未解決なら null)。 */
function cell(row: unknown[], idx: number | null): unknown {
  if (idx == null) return null;
  return row[idx] ?? null;
}

/**
 * 製品名から SKU を生成する。
 * ★ 既存カタログとの互換のためロジックを変更しないこと。
 *   変更すると「CSV一括上書き」の SKU 突合が全件外れ、
 *   報酬セットが参照する productId が総 orphan 化する。
 */
export function generateSku(name: string, existing: Set<string>): string {
  let base = name
    .normalize('NFKD')
    .replace(/[（）()「」]/g, '')
    .replace(/[\s/]+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .toUpperCase()
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (!base) base = 'PRODUCT';
  base = base.replace(/^TIMEWAVER-/, 'TW-').replace(/^MOBILEQUANTUM/, 'MQ');
  let sku = base;
  let i = 2;
  while (existing.has(sku)) {
    sku = `${base}-${i}`;
    i++;
  }
  existing.add(sku);
  return sku;
}

/** 集計行 (合計 / 平均 / 製品数) を弾く。 */
function isAggregateRow(name: string): boolean {
  return (
    name.includes('合計') ||
    name.includes('平均') ||
    name.includes('製品数') ||
    name.includes('小計')
  );
}

/**
 * シート全体をパースする。
 *
 * @param rows XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null }) の結果
 * @param options.defaultSupplierName 仕入先列が無いときに全行へ適用する既定値
 */
export function parseCatalogSheet(
  rows: unknown[][],
  options?: { defaultSupplierName?: string | null },
): ParsedSheet {
  const warnings: string[] = [];

  // ── ヘッダー行の探索: 先頭 20 行のうち最もスコアの高い行を採用 ──
  let headerRowIndex = -1;
  let best = { score: 0, cols: EMPTY_COLUMNS };
  const scanLimit = Math.min(rows.length, 20);
  for (let i = 0; i < scanLimit; i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;
    const cols = resolveColumns(row);
    const score = scoreColumns(cols);
    // 名前列 + 価格列が最低 1 つ揃って初めてヘッダー行とみなす
    if (score > best.score && cols.name != null && score >= 3) {
      best = { score, cols };
      headerRowIndex = i;
    }
  }

  let columns: ResolvedColumns;
  let startRow: number;
  let headerLabels: string[] = [];

  if (headerRowIndex >= 0) {
    columns = best.cols;
    startRow = headerRowIndex + 1;
    headerLabels = (rows[headerRowIndex] ?? []).map((c) => str(c) ?? '');
    if (columns.productCategory == null) {
      warnings.push(
        '「製品カテゴリ」列が見つかりませんでした（旧フォーマットの可能性）。製品カテゴリは空で取込まれます。',
      );
    }
    if (columns.costPrice == null) {
      warnings.push('「仕入価格 (JPY)」列が見つかりませんでした。仕入価格は 0 で取込まれます。');
    }
  } else {
    columns = LEGACY_COLUMNS;
    startRow = 0;
    warnings.push(
      'ヘッダー行を検出できませんでした。旧フォーマットの固定列 (A=製品名 / D=仕入価格 / E=税抜 / G=税込 / J=備考) として読み取ります。列がずれている場合は取込まないでください。',
    );
  }

  // ── データ行 ──
  const out: ParsedCatalogRow[] = [];
  const skuSet = new Set<string>();
  const defaultSupplier = options?.defaultSupplierName?.trim() || null;

  // SKU 列がある場合は先に全 SKU を確保しておく (自動生成分と衝突させないため)
  if (columns.sku != null) {
    for (let i = startRow; i < rows.length; i++) {
      const s = str(cell(rows[i] ?? [], columns.sku));
      if (s) skuSet.add(s);
    }
  }

  for (let i = startRow; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const name = str(cell(row, columns.name));
    if (!name) continue;
    if (isAggregateRow(name)) continue;

    const costPrice = num(cell(row, columns.costPrice));
    const priceExcludingTax = num(cell(row, columns.priceExcludingTax));
    const priceIncludingTax = num(cell(row, columns.priceIncludingTax));

    // 価格が 1 つも取れない行 = 区切り行・注記行とみなしてスキップ
    if (
      costPrice == null &&
      priceExcludingTax == null &&
      priceIncludingTax == null
    ) {
      continue;
    }

    const sheetSku = str(cell(row, columns.sku));
    const sku = sheetSku ?? generateSku(name, skuSet);

    let validationError: string | null = null;
    if (priceExcludingTax == null || priceExcludingTax < 0)
      validationError = '税抜価格が無効';
    else if (priceIncludingTax == null || priceIncludingTax < 0)
      validationError = '税込価格が無効';
    else if (costPrice != null && costPrice < 0) validationError = '仕入価格が無効';

    out.push({
      rowIndex: i,
      name,
      modelName: str(cell(row, columns.modelName)) ?? name,
      variation: str(cell(row, columns.variation)),
      sku,
      productCategory: str(cell(row, columns.productCategory)),
      description: str(cell(row, columns.description)),
      supplierName: str(cell(row, columns.supplierName)) ?? defaultSupplier,
      costPrice: costPrice ?? 0,
      priceExcludingTax: priceExcludingTax ?? 0,
      priceIncludingTax: priceIncludingTax ?? 0,
      validationError,
      skuFromSheet: sheetSku != null,
    });
  }

  // SKU 重複 (シート由来) を検出して警告 — 取込側は先勝ちで後続を捨てる
  if (columns.sku != null) {
    const seen = new Set<string>();
    const dup = new Set<string>();
    for (const r of out) {
      if (seen.has(r.sku)) dup.add(r.sku);
      seen.add(r.sku);
    }
    if (dup.size > 0) {
      warnings.push(
        `SKU が重複しています (${Array.from(dup).slice(0, 5).join(', ')}${dup.size > 5 ? ' ほか' : ''})。重複行は先頭の 1 件のみ取込まれます。`,
      );
    }
  }

  return { rows: out, headerRowIndex, columns, headerLabels, warnings };
}

/** 列マップを UI に出すためのラベル定義。 */
export const COLUMN_FIELD_LABELS: Record<FieldKey, string> = {
  name: '製品名',
  productCategory: '製品カテゴリ',
  modelName: '型番',
  variation: 'バリエーション',
  sku: 'SKU',
  costPrice: '仕入価格 (JPY)',
  priceExcludingTax: '税抜価格 (JPY)',
  priceIncludingTax: '税込価格 (JPY)',
  description: '備考',
  supplierName: '仕入先',
};

/** 0 → "A", 26 → "AA" (UI で列位置を示すため)。 */
export function columnLetter(index: number): string {
  let n = index;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}
